import { Button, Popconfirm, message } from "antd";
import dayjs from "dayjs";
import { BellRing, CalendarClock, PhoneMissed, PhoneOff, Phone, Hourglass, Wallet } from "lucide-react";
import {
    CONTACT_SLA_DAYS, assigneeOf, callCenterApi, customerTasks, formatMoney, invoiceDue, taskText, useCallCenter, type Customer,
} from "../../CustomerDemo/callCenterStore";
import { meName, softphone } from "../../CustomerDemo/softphone";
import { TaskBadges, TaskDoneButton } from "../../CustomerDemo/components/TaskWidgets";

/** Băng cảnh báo chặn gọi + khung "Việc cần làm" (công nợ, cuộc nhỡ, hẹn gọi lại tới hạn, quá hạn liên hệ) — lịch hẹn nằm ở CustomerJourney */
export default function CustomerTaskPanel({ customer, onOpenInvoices }: { customer: Customer; onOpenInvoices: () => void }) {
    const s = useCallCenter();
    const now = Date.now();
    const t = customerTasks(s, customer, now);
    const dnc = customer.doNotCall;
    const phone = customer.phones[0];
    const debt = t.debts.reduce((sum, x) => sum + invoiceDue(x), 0);
    // Khách bị chặn gọi thì ẩn việc gọi, nhưng công nợ vẫn phải theo dõi
    const showCalls = !dnc && (t.missed.length > 0 || t.callbacks.length > 0 || t.staleDays !== null);

    return (
        <>
            {dnc && (
                <div className="w-full rounded-2xl border border-[#fecaca] bg-[#fff7f7] dark:bg-[#2a1f1f] dark:border-[#4b2a2a] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fee2e2]"><PhoneOff className="h-5 w-5 text-[#dc2626]" /></div>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-[#b91c1c]">Đang chặn gọi khách này</div>
                        <div className="text-sm text-[#b91c1c]">Lý do: {dnc.reason} · {dnc.by} · {dayjs(dnc.at).format("HH:mm DD/MM/YYYY")}</div>
                        <div className="text-xs text-gray-500 mt-0.5">Tổng đài sẽ không cho gọi ra và khách không xuất hiện trong danh sách "Cần gọi lại".</div>
                    </div>
                    <Popconfirm title="Gỡ chặn gọi khách này?" okText="Gỡ chặn" cancelText="Huỷ"
                        onConfirm={() => { callCenterApi.setDoNotCall(customer.id, null, meName()); message.success("Đã gỡ chặn gọi"); }}>
                        <Button danger>Gỡ chặn</Button>
                    </Popconfirm>
                </div>
            )}

            {(showCalls || debt > 0) && (
                <div className="w-full rounded-2xl border border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900 p-4">
                    <div className="font-semibold text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2 mb-2"><BellRing size={15} /> Việc cần làm với khách</div>
                    <div className="space-y-2 text-sm">
                        {debt > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                <Wallet size={14} className="text-rose-500 shrink-0" />
                                <span className="flex-1 min-w-[200px] text-gray-700 dark:text-gray-200">
                                    Khách còn nợ <b className="text-rose-600">{formatMoney(debt)}</b> ở {t.debts.length} hoá đơn ({t.debts.map(x => x.code).join(", ")})
                                </span>
                                <Button size="small" type="primary" icon={<Wallet size={12} />} onClick={onOpenInvoices}>Thu tiền</Button>
                            </div>
                        )}
                        {!dnc && t.missed.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                                <PhoneMissed size={14} className="text-red-500 shrink-0" />
                                <span className="flex-1 min-w-[200px] text-gray-700 dark:text-gray-200">
                                    Khách gọi nhỡ <b>{t.missed.length}</b> lần chưa được gọi lại (gần nhất {dayjs(t.missed[0].startedAt).format("HH:mm DD/MM")})
                                </span>
                                <Button size="small" type="primary" icon={<Phone size={12} />} onClick={() => softphone.dial(phone)}>Gọi lại</Button>
                                <Button size="small" onClick={() => { callCenterApi.markHandled(t.missed[0].id); message.success("Đã đánh dấu xử lý"); }}>Đã xử lý</Button>
                            </div>
                        )}
                        {!dnc && t.callbacks.map(cb => {
                            const overdue = cb.dueAt < now;
                            return (
                                <div key={cb.id} className="flex flex-wrap items-center gap-2">
                                    <CalendarClock size={14} className={`shrink-0 ${overdue ? "text-rose-500" : "text-amber-600"}`} />
                                    <span className="flex-1 min-w-[200px] text-gray-700 dark:text-gray-200">
                                        <b className={overdue ? "text-rose-600" : ""}>{overdue ? "Quá hạn" : "Hạn"} {dayjs(cb.dueAt).format("HH:mm DD/MM")}</b> <TaskBadges cb={cb} /> {taskText(cb)} <span className="text-xs text-gray-400">· {assigneeOf(s, cb)}</span>
                                    </span>
                                    <Button size="small" type="primary" icon={<Phone size={12} />} onClick={() => softphone.dial(phone, { callbackId: cb.id })}>Gọi</Button>
                                    <TaskDoneButton cb={cb} />
                                </div>
                            );
                        })}
                        {!dnc && t.staleDays !== null && (
                            <div className="flex flex-wrap items-center gap-2">
                                <Hourglass size={14} className="text-orange-500 shrink-0" />
                                <span className="flex-1 min-w-[200px] text-gray-700 dark:text-gray-200">
                                    Đã <b>{t.staleDays} ngày</b> chưa liên hệ được với khách (chuẩn chăm sóc: tối đa {CONTACT_SLA_DAYS} ngày)
                                </span>
                                <Button size="small" type="primary" icon={<Phone size={12} />} onClick={() => softphone.dial(phone)}>Gọi chăm sóc</Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
